import { createSlice } from "@reduxjs/toolkit";
import {
  createModeratedPost,
  fetchPosts,
  fetchPostById,
  fetchPostByCustomId,
  fetchModerationStats,
  fetchPostsByUser,
  deletePostByCustomId, // existant
  updatePostByCustomId, // ✅ AJOUT SEULEMENT
} from "../thunks/moderationThunks";

const initialState = {
  // Posts data
  posts: [],
  selectedPost: null,

  // Stats
  stats: {
    totalPosts: 0,
    pendingPosts: 0,
    approvedPosts: 0,
    rejectedPosts: 0,
  },

  // UI loading states
  loading: {
    posts: false,
    create: false,
    stats: false,
  },

  // Errors
  error: null,
  errorMessage: null,

  // Pagination
  pagination: {

    hasMore: true,
  },
};

// Helper to ensure each post exposes a canonical `postId`
const normalizePost = (p = {}) => {
  if (!p || typeof p !== "object") return p;
  const resolved =
    p.postId ?? p.id_post ?? p.id_post ?? p.id ?? p._id ?? undefined;
  return { ...p, postId: resolved };
};

const moderationSlice = createSlice({
  name: "moderation",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.errorMessage = null;
    },
    clearPosts: (state) => {
      state.posts = [];
      state.pagination.skip = 0;
      state.pagination.hasMore = true;
    },
    clearSelectedPost: (state) => {
      state.selectedPost = null;
    },
    updatePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    /* ==================== CREATE POST ==================== */
    builder
      .addCase(createModeratedPost.pending, (state) => {
        state.loading.create = true;
        state.error = null;
        state.errorMessage = null;
      })
      .addCase(createModeratedPost.fulfilled, (state, action) => {
        state.loading.create = false;
        const rawPost = action.payload?.post;
        if (action.payload?.success === true && rawPost) {
          const newPost = normalizePost(rawPost);
          state.posts.unshift(newPost);
        }
      })
      .addCase(createModeratedPost.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload;
        // CreatePostBox/AskQuestionBox displays the refusal or technical error.
        // The feed retry panel is reserved for feed loading failures.
        state.errorMessage = null;
      });

    /* ==================== FETCH ALL POSTS ==================== */
    builder
      .addCase(fetchPosts.pending, (state) => {
        state.loading.posts = true;
        state.error = null;
        state.errorMessage = null;
      })
      .addCase(fetchPosts.fulfilled, (state, action) => {
        state.loading.posts = false;
        const payload = action.payload;
        let postsArray = [];

        if (Array.isArray(payload)) {
          postsArray = payload;
        } else if (payload?.data && Array.isArray(payload.data)) {
          postsArray = payload.data;
        } else if (payload?.posts && Array.isArray(payload.posts)) {
          postsArray = payload.posts;
        } else if (typeof payload === "object" && payload !== null) {
          const keys = Object.keys(payload);
          for (const key of keys) {
            if (Array.isArray(payload[key])) {
              postsArray = payload[key];
              break;
            }
          }
        }

        state.posts = postsArray.map((p) => normalizePost(p));
        if (payload?.pagination) {
          state.pagination = { ...state.pagination, ...payload.pagination };
        }
      })
      .addCase(fetchPosts.rejected, (state, action) => {
        state.loading.posts = false;
        state.error = action.payload;
        state.errorMessage = action.payload?.message || "Failed to fetch posts";
      });

    /* ==================== FETCH POSTS BY USER ==================== */
    builder
      .addCase(fetchPostsByUser.pending, (state) => {
        state.loading.posts = true;
        state.error = null;
        state.errorMessage = null;
      })
      .addCase(fetchPostsByUser.fulfilled, (state, action) => {
        state.loading.posts = false;
        const data = Array.isArray(action.payload) ? action.payload : [];
        state.posts = data.map((p) => normalizePost(p));
      })
      .addCase(fetchPostsByUser.rejected, (state, action) => {
        state.loading.posts = false;
        state.error = action.payload;
        state.errorMessage =
          action.payload?.message || "Failed to fetch user's posts";
      });

    /* ==================== FETCH POST BY ID ==================== */
    builder
      .addCase(fetchPostById.pending, (state) => {
        state.loading.posts = true;
        state.error = null;
      })
      .addCase(fetchPostById.fulfilled, (state, action) => {
        state.loading.posts = false;
        state.selectedPost = normalizePost(action.payload);
      })
      .addCase(fetchPostById.rejected, (state, action) => {
        state.loading.posts = false;
        state.error = action.payload;
        state.errorMessage = action.payload?.message || "Failed to fetch post";
      });

    /* ==================== FETCH POST BY CUSTOM ID ==================== */
    builder
      .addCase(fetchPostByCustomId.pending, (state) => {
        state.loading.posts = true;
      })
      .addCase(fetchPostByCustomId.fulfilled, (state, action) => {
        state.loading.posts = false;
        state.selectedPost = normalizePost(action.payload);
      })
      .addCase(fetchPostByCustomId.rejected, (state, action) => {
        state.loading.posts = false;
        state.error = action.payload;
        state.errorMessage =
          action.payload?.message || "Failed to fetch post by custom ID";
      });

    /* ==================== DELETE POST BY CUSTOM ID ==================== */
    builder
      .addCase(deletePostByCustomId.pending, (state) => {
        state.loading.posts = true;
        state.error = null;
      })
      .addCase(deletePostByCustomId.fulfilled, (state, action) => {
        state.loading.posts = false;
        const idPost = action.payload?.idPost;
        if (idPost) {
          state.posts = state.posts.filter(
            (p) => String(p.id_post ?? p.postId ?? p.id ?? p._id) !== String(idPost)
          );
        }
      })
      .addCase(deletePostByCustomId.rejected, (state, action) => {
        state.loading.posts = false;
        state.error = action.payload;
        state.errorMessage =
          action.payload?.message || "Failed to delete post";
      });

    /* ==================== UPDATE POST (AJOUT UNIQUE) ==================== */
    builder.addCase(updatePostByCustomId.fulfilled, (state, action) => {
      const updatedPost = action.payload?.post;
      if (!updatedPost) return;

      state.posts = state.posts.map((p) =>
        p.id_post === updatedPost.id_post ? updatedPost : p
      );

      if (state.selectedPost?.id_post === updatedPost.id_post) {
        state.selectedPost = updatedPost;
      }
    });

    /* ==================== FETCH STATS ==================== */
    builder
      .addCase(fetchModerationStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchModerationStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.stats = { ...state.stats, ...action.payload };
      })
      .addCase(fetchModerationStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload;
        state.errorMessage =
          action.payload?.message || "Failed to fetch moderation stats";
      });
  },
});

export const {
  clearError,
  clearPosts,
  clearSelectedPost,
  updatePagination,
} = moderationSlice.actions;

export default moderationSlice.reducer;
